/**
 * 批量事务抽屉 V4.0 - 主组件
 * 基于客户端状态追踪模式的重构实现
 */

import React, { useState, useCallback } from "react";
import { Drawer, Button, Spin, Row, Col, Space, Alert, Divider } from "antd";
import { SendOutlined, CloseOutlined, HistoryOutlined, MailOutlined, ShrinkOutlined, ExpandAltOutlined } from "@ant-design/icons";
import { TRANSACTION_TYPE_LABELS as TRANSACTION_TYPE_NAMES, getAllowedFlightHistoryCategories } from "../Monitor/constants/transactionConstants";
import { BatchTransactionProvider, useBatchTransactionContext } from "./context/BatchTransactionContext";
import TransactionHeader from "./components/TransactionHeader";
import AirportSupplierSelection from "./components/AirportSupplierSelection";
import TransactionTable from "./components/TransactionTable";
import SubmitValidationAlert from "./components/SubmitValidationAlert";
import FlightHistoryTimeline from "../../../components/FlightHistoryTimeline/FlightHistoryTimeline";
import UnifiedTransactionEmailDrawer from "../Monitor/components/UnifiedTransactionEmailDrawer";

/**
 * 批量事务处理抽屉主组件
 */
const BatchTransactionDrawer = ({ visible, onClose, selectedFlights = [], transactionType, onSuccess }) => {
    return (
        <BatchTransactionProvider initialFlights={selectedFlights} transactionType={transactionType}>
            <Drawer
                width={"80%"}
                open={visible}
                onClose={onClose}
                maskClosable={false}
                closable={false}
                styles={{
                    header: { borderBottom: "1px solid #f0f0f0" },
                }}
                title={<DrawerTitle transactionType={transactionType} onClose={onClose} onSuccess={onSuccess} />}
            >
                <DrawerBody selectedFlights={selectedFlights} />
            </Drawer>
        </BatchTransactionProvider>
    );
};

/**
 * 抽屉标题组件 (可以访问Context) - V5.0 重构版本
 */
const DrawerTitle = ({ transactionType, onClose, onSuccess }) => {
    const { state, transactionManager, orchestrator } = useBatchTransactionContext();

    const [isSubmitting, setIsSubmitting] = useState(false);

    // 邮件相关状态
    const [isEmailDrawerVisible, setIsEmailDrawerVisible] = useState(false);
    const [initialEmailRecipients, setInitialEmailRecipients] = useState({ to: [], cc: [] });

    const { selectedContractItems, selectedFlights, workflowMode, transactions, availableProducts, activeAirports, selectedSupplier } = state;

    // 处理邮件发送
    const handleSendEmail = useCallback(() => {
        const activeDetails = transactions.flatMap((t) => (t.details || []).filter((d) => d.clientStatus !== "deleted"));
        const to = new Set();
        const cc = new Set();

        // 从事务明细对应的可用产品中获取联系人
        activeDetails.forEach((detail) => {
            const availableProduct = availableProducts?.find((product) => product.id === detail.contractItemId);
            if (availableProduct && availableProduct.contact_emails) {
                availableProduct.contact_emails.forEach((emailInfo) => {
                    const email = typeof emailInfo === "string" ? emailInfo : emailInfo?.email;
                    if (email && email.trim()) {
                        const type = emailInfo?.type === "cc" ? "cc" : "to";
                        (type === "cc" ? cc : to).add(email.trim());
                    }
                });
            }
        });

        setInitialEmailRecipients({ to: Array.from(to), cc: Array.from(cc) });
        setIsEmailDrawerVisible(true);
    }, [transactions, availableProducts]);

    // 处理邮件抽屉关闭
    const handleEmailDrawerClose = useCallback(() => {
        setIsEmailDrawerVisible(false);
    }, []);

    // 处理邮件发送成功
    const handleEmailSuccess = useCallback((flightId) => {
        // 邮件发送成功后，只关闭邮件抽屉，不刷新任何数据
        setIsEmailDrawerVisible(false);
    }, []);

    // 处理提交
    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            const result = await transactionManager.submitTransactions((result) => {
                if (onSuccess) {
                    onSuccess(result);
                }
                // 智能刷新：保持用户上下文的数据刷新
                orchestrator.smartRefresh();
            });

            if (result.success) {
                console.log("✅ 提交成功:", result);
            }
        } catch (error) {
            console.error("❌ 提交失败:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <TitleBar transactionType={transactionType} onSubmit={handleSubmit} loading={isSubmitting} onClose={onClose} workflowMode={workflowMode} onSendEmail={handleSendEmail} canSubmit={transactionManager.canSubmit()} />

            {/* 邮件发送抽屉 */}
            <UnifiedTransactionEmailDrawer
                visible={isEmailDrawerVisible}
                onClose={handleEmailDrawerClose}
                selectedFlightIds={selectedFlights.map((f) => f.id)}
                allFlightsData={selectedFlights}
                transactionType={transactionType}
                selectedContractItems={selectedContractItems}
                onSuccess={handleEmailSuccess}
                initialToRecipients={initialEmailRecipients.to}
                initialCcRecipients={initialEmailRecipients.cc}
                // 传递批量事务上下文中的机场和供应商信息
                contextAirports={activeAirports}
                contextSupplier={selectedSupplier}
            />
        </>
    );
};

/**
 * 抽屉内容组件 (可以访问Context)
 */
const DrawerBody = ({ selectedFlights }) => {
    const { state, orchestrator } = useBatchTransactionContext();
    const { loading, transactions, dataLoadingStatus, errors, transactionType } = state;

    const [selectedFlightForHistory, setSelectedFlightForHistory] = useState(null);
    const [historyCollapsed, setHistoryCollapsed] = useState(false);

    // 数据加载完成后，默认选择第一个事务的航班历史
    React.useEffect(() => {
        if (dataLoadingStatus === "SUCCESS") {
            if (transactions.length > 0) {
                const firstTransaction = transactions[0];
                // 如果第一个事务是新建的（ID不是数字），则不显示历史
                if (typeof firstTransaction.id !== "number") {
                    setSelectedFlightForHistory(null);
                } else {
                    const firstFlight = selectedFlights.find((f) => f.id === firstTransaction.flightId);
                    setSelectedFlightForHistory(firstFlight || null);
                }
            } else {
                // 如果没有事务，清空历史记录
                setSelectedFlightForHistory(null);
            }
        }
    }, [dataLoadingStatus, transactions, selectedFlights]);

    const handleViewFlightHistory = useCallback(
        (flight) => {
            setSelectedFlightForHistory(flight);
            // 如果历史区域是收缩状态，自动展开
            if (historyCollapsed) {
                setHistoryCollapsed(false);
            }
        },
        [historyCollapsed],
    );

    const handleToggleHistory = useCallback(() => {
        setHistoryCollapsed(!historyCollapsed);
    }, [historyCollapsed]);

    return (
        <Spin spinning={loading}>
            <Row gutter={0} style={{ height: "100%" }}>
                {/* Left side - 动态调整宽度 */}
                <Col span={historyCollapsed ? 24 : 16} style={{ paddingRight: historyCollapsed ? 0 : 12 }}>
                    <div>
                        {/* 错误提示 */}
                        {errors && Object.keys(errors).length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                {Object.entries(errors).map(([key, error]) => (
                                    <Alert key={key} message="系统错误" description={error} type="error" showIcon closable style={{ marginBottom: 8 }} />
                                ))}
                            </div>
                        )}

                        <TransactionHeader />
                        <AirportSupplierSelection />
                        <SubmitValidationAlert />
                        <TransactionTable onViewFlightHistory={handleViewFlightHistory} selectedFlightForHistory={selectedFlightForHistory} />
                    </div>
                </Col>
                {/* Right side - 可收缩的航班历史区域 */}
                {!historyCollapsed && (
                    <Col span={8} style={{ paddingLeft: 12 }}>
                        <div style={{ height: "100%", borderLeft: "1px solid #f0f0f0", paddingLeft: 16 }}>
                            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Space>
                                    <HistoryOutlined style={{ color: "#1890ff" }} />
                                    <span style={{ fontWeight: "bold" }}>航班历史</span>
                                </Space>
                                <Button size="small" type="text" icon={<ShrinkOutlined />} onClick={handleToggleHistory} title="收缩历史区域" />
                            </div>
                            {selectedFlightForHistory ? (
                                <FlightHistoryTimeline key={`${selectedFlightForHistory.id}-${orchestrator.historyRefreshTrigger}`} flightId={selectedFlightForHistory.id} compact={true} allowedCategories={getAllowedFlightHistoryCategories(transactionType)} />
                            ) : (
                                <div style={{ textAlign: "center", color: "#999", padding: "40px 0" }}>点击事务行查看对应航班的历史记录</div>
                            )}
                        </div>
                    </Col>
                )}
                {/* 收缩状态下的展开按钮 */}
                {historyCollapsed && (
                    <div
                        style={{
                            position: "fixed",
                            right: 24,
                            top: "12%",
                            transform: "translateY(-50%)",
                            zIndex: 1000,
                        }}
                    >
                        <Button
                            type="primary"
                            icon={<ExpandAltOutlined />}
                            onClick={handleToggleHistory}
                            title="历史"
                            style={{
                                textOrientation: "mixed",
                                height: 80,
                                width: "7em",
                                borderRadius: "8px 0 0 8px",
                            }}
                        >
                            查看历史
                        </Button>
                    </div>
                )}
            </Row>
        </Spin>
    );
};

/**
 * 标题栏组件
 */
const TitleBar = ({ transactionType, onSubmit, loading, onClose, workflowMode, onSendEmail, canSubmit }) => {
    const { transactionManager } = useBatchTransactionContext();

    // 根据工作流模式决定按钮文本和图标
    const getButtonConfig = () => {
        switch (workflowMode) {
            case "EDIT":
                return { text: "保存修改", icon: <SendOutlined /> };
            case "MIXED":
                return { text: "保存并创建", icon: <SendOutlined /> };
            case "CREATE":
            default:
                return { text: "提交创建事务", icon: <SendOutlined /> };
        }
    };

    const buttonConfig = getButtonConfig();
    const changesSummary = transactionManager.getChangesSummary();

    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <span>{`批量${TRANSACTION_TYPE_NAMES[transactionType] || "事务处理"}`}</span>
                {workflowMode === "EDIT" && changesSummary.total > 0 && (
                    <span style={{ color: "#1890ff", fontSize: "12px" }}>
                        ({changesSummary.new > 0 && `新增${changesSummary.new}`}
                        {changesSummary.modified > 0 && `${changesSummary.new > 0 ? ", " : ""}修改${changesSummary.modified}`}
                        {changesSummary.deleted > 0 && `${changesSummary.new > 0 || changesSummary.modified > 0 ? ", " : ""}删除${changesSummary.deleted}`})
                    </span>
                )}
            </div>
            <Space>
                <Button type="primary" ghost icon={<MailOutlined />} onClick={onSendEmail}>
                    去发邮件
                </Button>
                <Button type="primary" icon={buttonConfig.icon} disabled={!canSubmit} loading={loading} onClick={onSubmit}>
                    {buttonConfig.text}
                </Button>
                <Button icon={<CloseOutlined />} onClick={onClose}>
                    关闭
                </Button>
            </Space>
        </div>
    );
};

export default BatchTransactionDrawer;
